import { Routes } from "@angular/router";
import { authGuard, loginGuard, portalGuard } from "./core/auth.guard";
import { platformGuard, platformLoginGuard } from "./core/platform.guard";

export const routes: Routes = [
  {
    path: "",
    pathMatch: "full",
    canActivate: [loginGuard],
    loadComponent: () =>
      import("./pages/landing.component").then((m) => m.LandingComponent),
  },
  {
    path: "login",
    canActivate: [loginGuard],
    loadComponent: () =>
      import("./pages/login.component").then((m) => m.LoginComponent),
  },
  {
    path: "signup",
    canActivate: [loginGuard],
    loadComponent: () =>
      import("./pages/signup.component").then((m) => m.SignupComponent),
  },
  {
    path: "sign/:token",
    loadComponent: () =>
      import("./pages/sign.component").then((m) => m.SignComponent),
  },
  {
    path: "invoice/:token",
    loadComponent: () =>
      import("./pages/invoice-view.component").then((m) => m.InvoiceViewComponent),
  },
  {
    path: "reset-password",
    loadComponent: () =>
      import("./pages/reset-password.component").then(
        (m) => m.ResetPasswordComponent,
      ),
  },
  {
    path: "dashboard",
    canActivate: [authGuard],
    data: { perm: "office" },
    loadComponent: () =>
      import("./pages/dashboard.component").then((m) => m.DashboardComponent),
  },
  {
    path: "team",
    canActivate: [authGuard],
    loadComponent: () =>
      import("./pages/team-messages.component").then(
        (m) => m.TeamMessagesComponent,
      ),
  },
  {
    path: "my",
    canActivate: [authGuard],
    loadComponent: () =>
      import("./pages/my-work.component").then((m) => m.MyWorkComponent),
  },
  {
    path: "my/projects/:id",
    canActivate: [authGuard],
    loadComponent: () =>
      import("./pages/my/my-project.component").then((m) => m.MyProjectComponent),
  },
  {
    path: "clients",
    canActivate: [authGuard],
    data: { perm: "clients.manage" },
    loadComponent: () =>
      import("./pages/clients.component").then((m) => m.ClientsComponent),
  },
  {
    path: "projects",
    canActivate: [authGuard],
    data: { perm: "projects.manage" },
    loadComponent: () =>
      import("./pages/projects.component").then((m) => m.ProjectsComponent),
  },
  {
    path: "projects/:id",
    canActivate: [authGuard],
    data: { perm: "projects.manage" },
    loadComponent: () =>
      import("./pages/project-workspace.component").then(
        (m) => m.ProjectWorkspaceComponent,
      ),
    children: [
      { path: "", pathMatch: "full", redirectTo: "overview" },
      {
        path: "overview",
        loadComponent: () =>
          import("./pages/project/project-overview.component").then(
            (m) => m.ProjectOverviewComponent,
          ),
      },
      {
        path: "dashboard",
        loadComponent: () =>
          import("./pages/project/project-dashboard.component").then(
            (m) => m.ProjectDashboardComponent,
          ),
      },
      {
        path: "documents",
        loadComponent: () =>
          import("./pages/project/project-documents.component").then(
            (m) => m.ProjectDocumentsComponent,
          ),
      },
      {
        path: "takeoff",
        loadComponent: () =>
          import("./pages/project/project-takeoff.component").then(
            (m) => m.ProjectTakeoffComponent,
          ),
      },
      {
        path: "tasks",
        loadComponent: () =>
          import("./pages/project/project-tasks.component").then(
            (m) => m.ProjectTasksComponent,
          ),
      },
      {
        path: "team",
        loadComponent: () =>
          import("./pages/project/project-team.component").then(
            (m) => m.ProjectTeamComponent,
          ),
      },
      {
        path: "communication",
        loadComponent: () =>
          import("./pages/project/project-communication.component").then(
            (m) => m.ProjectCommunicationComponent,
          ),
      },
      {
        path: "payments",
        loadComponent: () =>
          import("./pages/project/project-payments.component").then(
            (m) => m.ProjectPaymentsComponent,
          ),
      },
    ],
  },
  {
    path: "subcontractors",
    canActivate: [authGuard],
    data: { perm: "subcontractors.manage" },
    loadComponent: () =>
      import("./pages/subcontractors.component").then(
        (m) => m.SubcontractorsComponent,
      ),
  },
  {
    path: "estimates",
    canActivate: [authGuard],
    data: { perm: "estimates.manage" },
    loadComponent: () =>
      import("./pages/estimates.component").then((m) => m.EstimatesComponent),
  },
  {
    path: "contracts",
    canActivate: [authGuard],
    data: { perm: "contracts.manage" },
    loadComponent: () =>
      import("./pages/contracts.component").then((m) => m.ContractsComponent),
  },
  {
    path: "proposals",
    canActivate: [authGuard],
    data: { perm: "proposals.manage" },
    loadComponent: () =>
      import("./pages/proposals/proposals.component").then(
        (m) => m.ProposalsComponent,
      ),
  },
  {
    path: "proposals/new",
    canActivate: [authGuard],
    data: { perm: "proposals.manage" },
    loadComponent: () =>
      import("./pages/proposals/proposal-capture.component").then(
        (m) => m.ProposalCaptureComponent,
      ),
  },
  {
    path: "proposals/:id",
    canActivate: [authGuard],
    data: { perm: "proposals.manage" },
    loadComponent: () =>
      import("./pages/proposals/proposal-editor.component").then(
        (m) => m.ProposalEditorComponent,
      ),
  },
  {
    path: "scheduling",
    canActivate: [authGuard],
    data: { perm: "scheduling.manage" },
    loadComponent: () =>
      import("./pages/scheduling.component").then((m) => m.SchedulingComponent),
  },
  {
    path: "communication",
    canActivate: [authGuard],
    data: { perm: "communication.manage" },
    loadComponent: () =>
      import("./pages/communication.component").then(
        (m) => m.CommunicationComponent,
      ),
  },
  {
    path: "tasks",
    canActivate: [authGuard],
    data: { perm: "tasks.manage" },
    loadComponent: () =>
      import("./pages/tasks.component").then((m) => m.TasksComponent),
  },
  {
    path: "crm",
    canActivate: [authGuard],
    data: { perm: "marketing.manage" },
    loadComponent: () =>
      import("./pages/marketing/crm.component").then((m) => m.CrmComponent),
  },
  {
    path: "website",
    canActivate: [authGuard],
    data: { perm: "marketing.manage" },
    loadComponent: () =>
      import("./pages/marketing/website-builder.component").then(
        (m) => m.WebsiteBuilderComponent,
      ),
  },
  {
    path: "marketing",
    canActivate: [authGuard],
    data: { perm: "marketing.manage" },
    loadComponent: () =>
      import("./pages/marketing/marketing.component").then(
        (m) => m.MarketingComponent,
      ),
  },
  {
    path: "employees",
    canActivate: [authGuard],
    data: { perm: "employees.manage" },
    loadComponent: () =>
      import("./pages/employees.component").then((m) => m.EmployeesComponent),
  },
  {
    path: "timesheets",
    canActivate: [authGuard],
    data: { perm: "employees.manage" },
    loadComponent: () =>
      import("./pages/timesheets.component").then((m) => m.TimesheetsComponent),
  },
  {
    path: "roles",
    canActivate: [authGuard],
    data: { perm: "roles.manage" },
    loadComponent: () =>
      import("./pages/roles.component").then((m) => m.RolesComponent),
  },
  {
    path: "estimating",
    canActivate: [authGuard],
    data: { perm: "admin" },
    loadComponent: () =>
      import("./pages/estimating.component").then(
        (m) => m.EstimatingComponent,
      ),
  },
  {
    path: "settings",
    canActivate: [authGuard],
    data: { perm: "admin" },
    loadComponent: () =>
      import("./pages/settings.component").then((m) => m.SettingsComponent),
  },
  {
    path: "landing-content",
    canActivate: [authGuard],
    data: { perm: "admin" },
    loadComponent: () =>
      import("./pages/landing-content.component").then(
        (m) => m.LandingContentComponent,
      ),
  },
  {
    path: "billing",
    canActivate: [authGuard],
    data: { perm: "admin" },
    loadComponent: () =>
      import("./pages/billing.component").then((m) => m.BillingComponent),
  },
  {
    path: "portal",
    canActivate: [portalGuard],
    loadComponent: () =>
      import("./pages/portal/portal-layout.component").then(
        (m) => m.PortalLayoutComponent,
      ),
    children: [
      {
        path: "",
        pathMatch: "full",
        loadComponent: () =>
          import("./pages/portal/portal-dashboard.component").then(
            (m) => m.PortalDashboardComponent,
          ),
      },
      {
        path: "invoices",
        loadComponent: () =>
          import("./pages/portal/portal-invoices.component").then(
            (m) => m.PortalInvoicesComponent,
          ),
      },
      {
        path: "messages",
        loadComponent: () =>
          import("./pages/portal/portal-messages.component").then(
            (m) => m.PortalMessagesComponent,
          ),
      },
      {
        path: "projects/:id",
        loadComponent: () =>
          import("./pages/portal/portal-project.component").then(
            (m) => m.PortalProjectComponent,
          ),
      },
      {
        path: "security",
        loadComponent: () =>
          import("./pages/portal/portal-security.component").then(
            (m) => m.PortalSecurityComponent,
          ),
      },
    ],
  },
  {
    path: "platform/login",
    canActivate: [platformLoginGuard],
    loadComponent: () =>
      import("./pages/platform/platform-login.component").then(
        (m) => m.PlatformLoginComponent,
      ),
  },
  {
    path: "platform",
    canActivate: [platformGuard],
    loadComponent: () =>
      import("./pages/platform/platform-layout.component").then(
        (m) => m.PlatformLayoutComponent,
      ),
    children: [
      { path: "", pathMatch: "full", redirectTo: "overview" },
      {
        path: "overview",
        loadComponent: () =>
          import("./pages/platform/platform-overview.component").then(
            (m) => m.PlatformOverviewComponent,
          ),
      },
      {
        path: "companies",
        loadComponent: () =>
          import("./pages/platform/platform-companies.component").then(
            (m) => m.PlatformCompaniesComponent,
          ),
      },
      {
        path: "members",
        loadComponent: () =>
          import("./pages/platform/platform-members.component").then(
            (m) => m.PlatformMembersComponent,
          ),
      },
      {
        path: "announcements",
        loadComponent: () =>
          import("./pages/platform/platform-announcements.component").then(
            (m) => m.PlatformAnnouncementsComponent,
          ),
      },
      {
        path: "plans",
        loadComponent: () =>
          import("./pages/platform/platform-plans.component").then(
            (m) => m.PlatformPlansComponent,
          ),
      },
      {
        path: "audit",
        loadComponent: () =>
          import("./pages/platform/platform-audit.component").then(
            (m) => m.PlatformAuditComponent,
          ),
      },
    ],
  },
  { path: "**", redirectTo: "dashboard" },
];
